import { forwardRef, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { AuthoredGlyph } from './AuthoredGlyph.js';
import { CardGlyph } from './CardGlyph.js';
import { PixelPattern } from './PixelPattern.js';
import { PixelIcon } from './PixelIcon.js';
import type { CardInfoData } from './CardInfoCard.js';
import { cardName, cardTypeLabel } from './locale.js';
import { ScrollingCardName } from './ScrollingCardName.js';
import { Badge } from './kit/index.js';
import { useCardTilt } from './useCardTilt.js';

export interface MiniCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  data?: CardInfoData;
  name?: string;
  testId: string;
  action?: ReactNode;
  count?: ReactNode;
  unavailableLabel?: string;
  /** Hide the card-type line when the surrounding surface already names it. */
  hideMeta?: boolean;
  /** Compact cards omit their redundant type label (e.g. "Боец"). */
  hideTypeLabel?: boolean;
  /** Maximum lines for a passive name before it switches to the shared marquee. */
  passiveNameMaxLines?: number;
}

/** Canonical compact card renderer. Routes own hover/dialog behavior and actions. */
export const MiniCard = forwardRef<HTMLDivElement, MiniCardProps>(function MiniCard({
  data,
  name = data === undefined ? '' : data.name ?? cardName(data.defId),
  testId,
  action,
  count,
  unavailableLabel = 'Недоступна',
  hideMeta = false,
  hideTypeLabel = true,
  passiveNameMaxLines,
  className,
  ...props
}: MiniCardProps, ref): ReactElement {
  const { ref: tiltRef, ...tilt } = useCardTilt();
  const isPassive = data?.kind === 'deck-passive';
  const isSpell = data?.type === 'SPELL';
  const hasStats = data?.attack !== undefined && data.health !== undefined;
  const showMeta = !hideMeta && (isSpell || !isPassive || !hideTypeLabel);

  function mergedRef(element: HTMLDivElement | null): void {
    tiltRef(element);
    if (typeof ref === 'function') ref(element);
    else if (ref !== null) ref.current = element;
  }

  return (
    <div
      {...props}
      {...tilt}
      ref={mergedRef}
      className={['cards-mini-card', 'cards-tilt', className].filter(Boolean).join(' ')}
      data-card-type={data?.type}
      data-card-kind={isPassive ? 'passive' : undefined}
      data-hide-meta={hideMeta ? 'true' : undefined}
      data-testid={testId}
    >
      <span className="cards-tilt-glare" aria-hidden="true" />
      {isPassive ? <Badge tone="neutral" className="cards-mini-card-kind" aria-hidden="true">Пассивка</Badge> : null}
      {data === undefined ? (
        <div className="cards-mini-card-unavailable">
          <strong>{name}</strong>
          <span>{unavailableLabel}</span>
        </div>
      ) : (
        <>
          {data.pattern === undefined ? null : (
            <PixelPattern
              pattern={data.pattern}
              id={`${testId}-pattern`}
              testId={`${testId}-pattern`}
              className="cards-card-detail-pattern"
            />
          )}
          {!showMeta ? null : (
            <div className="cards-mini-card-meta">
              {hideTypeLabel ? null : (
                <span title={data.metaLabel ?? cardTypeLabel(data.type)}>{data.metaLabel ?? cardTypeLabel(data.type)}</span>
              )}
              {isPassive ? null : <span
                className="cards-mini-card-energy"
                aria-label={`Энергия: ${data.cost ?? '–'}`}
                title={`Энергия: ${data.cost ?? '–'}`}
              >
                <span aria-hidden="true"><PixelIcon name="energy" /></span>{data.cost ?? '–'}
              </span>}
            </div>
          )}
          <div className="cards-mini-card-art" aria-hidden="true">
            {data.glyph === undefined ? (
              <CardGlyph defId={data.defId} size={48} />
            ) : (
              <AuthoredGlyph glyph={data.glyph} testId={`${testId}-glyph`} visualId={data.defId} size={48} />
            )}
          </div>
          {isPassive ? null : <ScrollingCardName
            name={name}
            className="cards-mini-card-name"
            trackClassName="cards-mini-card-name-track"
          />}
          {isSpell ? null : (
            <div className="cards-mini-card-stats">
              {isPassive ? (
                <ScrollingCardName
                  name={name}
                  className="cards-mini-card-name cards-mini-card-passive-name"
                  trackClassName="cards-mini-card-name-track"
                  {...(passiveNameMaxLines === undefined ? {} : { maxLines: passiveNameMaxLines })}
                />
              ) : !hasStats ? (
                <span aria-hidden="true">—</span>
              ) : (
                <>
                  <span aria-label={`Атака: ${data.attack}`}>
                    <span aria-hidden="true"><PixelIcon name="attack" /></span> {data.attack}
                  </span>
                  <span aria-label={`Здоровье: ${data.health}`}>
                    <span aria-hidden="true"><PixelIcon name="health" /></span> {data.health}
                  </span>
                </>
              )}
            </div>
          )}
        </>
      )}
      {count === undefined ? null : (
        <div className="cards-mini-card-count">{count}</div>
      )}
      {action}
    </div>
  );
});
